import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Box, 
  Grid, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPartnerGyms, deleteGym } from '../../redux/gymSlice';
import { RootState, AppDispatch } from '../../redux/store';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';

export const GymsListPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { gyms, isLoading, error } = useSelector((state: RootState) => state.gym);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [gymToDelete, setGymToDelete] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchPartnerGyms());
  }, [dispatch]);

  const handleDeleteClick = (gymId: number) => {
    setGymToDelete(gymId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (gymToDelete) {
      await dispatch(deleteGym(gymToDelete));
      setDeleteDialogOpen(false);
      setGymToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setGymToDelete(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress sx={{ color: '#14B8A6' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            My Gyms
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your gym locations and details
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => navigate('/gyms/create')}
          sx={{
            height: '48px',
            minWidth: { xs: '100%', sm: '180px' },
            backgroundColor: '#14B8A6',
            '&:hover': {
              backgroundColor: '#0F9688'
            }
          }}
        >
          Add New Gym
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {gyms.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" sx={{ color: '#666', mb: 2, fontWeight: 600 }}>
            No Gyms Yet
          </Typography>
          <Typography variant="body1" sx={{ color: '#999', mb: 4 }}>
            Create your first gym to start managing bookings and slots
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => navigate('/gyms/create')}
            sx={{
              height: '48px',
              px: 4,
              backgroundColor: '#14B8A6',
              '&:hover': {
                backgroundColor: '#0F9688'
              }
            }}
          >
            Create First Gym
          </Button>
        </Box>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {gyms.map((gym) => (
            <Grid item xs={12} sm={6} md={4} key={gym.id}>
              <Box 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2.5,
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    borderColor: '#14B8A6',
                    boxShadow: '0 2px 8px rgba(20, 184, 166, 0.1)'
                  }
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, pr: 1, fontSize: '1.1rem' }}>
                      {gym.name}
                    </Typography>
                    <Chip
                      label={gym.is_active ? 'Active' : 'Inactive'}
                      color={gym.is_active ? 'success' : 'default'}
                      size="small"
                      sx={{ fontWeight: 500, height: '24px' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#666' }}>
                    <LocationOnIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {gym.city}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.75rem' }}>
                        Rating
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                        {Number(gym.rating || 0).toFixed(1)} ⭐
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.75rem' }}>
                        Reviews
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                        {gym.total_reviews || 0}
                      </Typography>
                    </Box>
                  </Box>

                  {gym.is_verified ? (
                    <Chip 
                      label="Verified" 
                      color="success" 
                      size="small" 
                      sx={{ fontWeight: 500, height: '24px' }}
                    />
                  ) : (
                    <Chip 
                      label="Pending" 
                      color="warning" 
                      size="small"
                      sx={{ fontWeight: 500, height: '24px' }}
                    />
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #f0f0f0' }}>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => navigate(`/gyms/${gym.id}`)}
                    sx={{ flex: 1, fontSize: '0.8125rem' }}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/gyms/${gym.id}/edit`)}
                    sx={{ flex: 1, fontSize: '0.8125rem' }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DeleteIcon />}
                    color="error"
                    onClick={() => handleDeleteClick(gym.id)}
                    sx={{ flex: 1, fontSize: '0.8125rem' }}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Delete Gym?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this gym? This action will deactivate the gym and it won't be visible to users. This action can be reversed by contacting support.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleDeleteCancel}
            sx={{ height: '40px' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            sx={{ height: '40px' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GymsListPage;
